import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Layers, Link as LinkIcon, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductModifiersTab({ product, authAxios }) {
  const [allModifierGroups, setAllModifierGroups] = useState([]);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [localGroupIds, setLocalGroupIds] = useState(product?.modifier_group_ids || []);

  useEffect(() => {
    if (product) {
      authAxios.get("/menu/modifier-groups").then(res => setAllModifierGroups(res.data)).catch(() => {});
    }
  }, [product, authAxios]);

  const assignedGroups = allModifierGroups.filter(g => localGroupIds.includes(g.id));
  const unassignedGroups = allModifierGroups.filter(g => !localGroupIds.includes(g.id));

  const assignGroup = async (groupId) => {
    try {
      await authAxios.post(`/menu/products/${product.id}/modifier-groups`, { group_id: groupId });
      const newIds = [...localGroupIds, groupId];
      product.modifier_group_ids = newIds;
      setLocalGroupIds(newIds);
      setShowAssignPicker(false);
      toast.success("Grupo asignado");
    } catch { toast.error("Error"); }
  };

  const removeGroup = async (groupId) => {
    try {
      await authAxios.delete(`/menu/products/${product.id}/modifier-groups/${groupId}`);
      const newIds = localGroupIds.filter(id => id !== groupId);
      product.modifier_group_ids = newIds;
      setLocalGroupIds(newIds);
      toast.success("Grupo removido");
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-4 mt-2 pr-2">
      <p className="text-sm text-gray-500">Grupos de modificadores permiten personalizar el producto (ej: elegir proteina, tamano, extras).</p>
      {assignedGroups.length > 0 ? (
        <div className="space-y-2">
          {assignedGroups.map(g => (
            <div key={g.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div>
                <span className="font-bold text-emerald-800">{g.name}</span>
                {g.required && <span className="ml-2 text-xs text-orange-600">(obligatorio)</span>}
                <span className="ml-2 text-xs text-gray-500">{g.min_select === g.max_select ? `Elegir ${g.min_select}` : `${g.min_select}-${g.max_select}`}</span>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {(g.options || []).slice(0, 6).map((opt, i) => (
                    <span key={i} className="text-xs bg-white text-gray-600 px-1.5 py-0.5 rounded">
                      {opt.name} {opt.price > 0 ? `+${opt.price.toFixed(2)}EUR` : ""}
                    </span>
                  ))}
                  {(g.options || []).length > 6 && <span className="text-xs text-gray-400">+{g.options.length - 6} mas</span>}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => removeGroup(g.id)}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Quitar
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-400">
          <Layers className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Sin grupos de modificadores</p>
          <p className="text-xs mt-1">Asigna grupos para que el cliente personalice este producto</p>
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => setShowAssignPicker(true)} data-testid="assign-modifier-group">
          <LinkIcon className="w-4 h-4 mr-1" /> Asignar Grupo de Modificadores
        </Button>
      </div>
      <Dialog open={showAssignPicker} onOpenChange={setShowAssignPicker}>
        <DialogContent>
          <DialogHeader><DialogTitle>Asignar Grupo de Modificadores</DialogTitle></DialogHeader>
          <div className="max-h-60 overflow-auto space-y-1">
            {unassignedGroups.length === 0
              ? <p className="text-center text-gray-400 py-4">No hay grupos disponibles. Crea grupos en la pestana "Variaciones" del Menu.</p>
              : unassignedGroups.map(g => (
                <button key={g.id} className="w-full text-left px-3 py-2.5 rounded hover:bg-gray-50 flex items-center justify-between" onClick={() => assignGroup(g.id)}>
                  <div>
                    <span className="font-medium">{g.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{(g.options || []).length} opciones</span>
                    {g.required && <span className="text-xs text-orange-500 ml-2">(obligatorio)</span>}
                  </div>
                  <Plus className="w-4 h-4 text-emerald-600" />
                </button>
              ))
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
